import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Minus, ShoppingBag, Calendar, Clock, User, Phone, Mail, MessageSquare, CheckCircle2, Loader2, ArrowRight } from 'lucide-react'

export default function KagerBrodPage() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [cart, setCart] = useState({}) // { [productId]: quantity }
  const [activeCategory, setActiveCategory] = useState('alle')
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    pickupDate: '',
    pickupTime: '',
    comments: '',
    website_url: '' // Honeypot
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submissionDetails, setSubmissionDetails] = useState(null)

  useEffect(() => {
    // Set SEO Meta
    document.title = 'Bestil Kager & Brød | Craft Café Horsens'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Bestil friskbagt surdejsbrød, rugbrød, kanelsnegle og lækre kager online fra Craft Café i Horsens. Nem afhentning efter aftale.'
      )
    }

    // Fetch bakery items from CMS
    fetch('https://api.dev.auxo.dk/public/content/bakery_items')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch bakery items')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setProducts(data)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching bakery items:', err)
        setLoading(false)
      })
  }, [])

  // Cart helper functions
  const updateQuantity = (id, delta) => {
    setCart((prev) => {
      const current = prev[id] || 0
      const next = current + delta
      if (next <= 0) {
        const updated = { ...prev }
        delete updated[id]
        return updated
      }
      return { ...prev, [id]: next }
    })
  }

  const getCartTotal = () => {
    return Object.entries(cart).reduce((total, [id, qty]) => {
      const product = products.find((p) => p.id === id)
      return total + (product ? product.price * qty : 0)
    }, 0)
  }

  const getCartCount = () => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0)
  }

  // Filter products by category
  const filteredProducts = products.filter((product) => {
    if (activeCategory === 'alle') return true
    const terms = product.terms?.['bakery-category'] || []
    return terms.includes(activeCategory)
  })

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Check honeypot
    if (formData.website_url) {
      // Bot detected, silently ignore or show fake success
      setSubmitSuccess(true)
      return
    }

    if (getCartCount() === 0) {
      setSubmitError('Vælg venligst mindst ét produkt til din bestilling.')
      return
    }

    setSubmitting(true)
    setSubmitError('')

    // Format order items for the email submission
    const orderItemsText = Object.entries(cart)
      .map(([id, qty]) => {
        const product = products.find((p) => p.id === id)
        return product ? `${product.title} x ${qty} (${product.price * qty} kr.)` : ''
      })
      .filter(Boolean)
      .join(', ')

    const totalPriceText = `${getCartTotal()} kr.`

    const payload = {
      website_url: formData.website_url, // Honeypot
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      pickup_date: formData.pickupDate,
      pickup_time: formData.pickupTime,
      comments: formData.comments,
      order_items: orderItemsText,
      total_price: totalPriceText,
      source: 'Kager & Brød Bestillingsside'
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error('Der skete en fejl ved afsendelse af din bestilling. Prøv igen eller ring til os.')
      }

      const resData = await response.json()
      
      setSubmissionDetails({
        items: Object.entries(cart).map(([id, qty]) => {
          const product = products.find((p) => p.id === id)
          return { ...product, quantity: qty }
        }),
        total: getCartTotal(),
        pickupDate: formData.pickupDate,
        pickupTime: formData.pickupTime,
        name: formData.name
      })

      setSubmitSuccess(true)
      setCart({}) // Clear cart
      setFormData({
        name: '',
        phone: '',
        email: '',
        pickupDate: '',
        pickupTime: '',
        comments: '',
        website_url: ''
      })
    } catch (err) {
      setSubmitError(err.message || 'Der skete en uventet fejl. Prøv venligst igen.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="pt-[70px] md:pt-[100px]">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#faf9f6] text-[#1c2b21] border-b border-stone-200">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/9c2b893a-626f-4e94-8569-e35755676f4e.jpg"
            alt="Craft Café Kager & Brød"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#faf9f6] via-[#faf9f6]/40 to-[#faf9f6]" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 py-20 md:py-28 text-center space-y-6">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#b89a6c] font-light">
            Craft Café
          </span>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide leading-relaxed text-[#1c2b21]">
            Bestilling af Kager & Brød
          </h1>
          <p className="text-xs md:text-sm text-stone-600 max-w-2xl mx-auto leading-relaxed font-light uppercase tracking-wider">
            Friskbagt surdejsbrød, sprøde rundstykker og lækre kager fra vores eget bageri. Bestil her og afhent i caféen.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-6">
        {submitSuccess && submissionDetails ? (
          /* Success State */
          <div className="max-w-2xl mx-auto border border-stone-200 bg-white p-8 md:p-12 text-center space-y-8 rounded-none">
            <div className="inline-flex items-center justify-center border border-[#b89a6c]/20 p-4 text-[#b89a6c] bg-[#b89a6c]/5 rounded-full">
              <CheckCircle2 size={40} />
            </div>
            <div className="space-y-3">
              <h2 className="font-serif text-2xl md:text-3xl font-light text-[#1c2b21]">Tusind tak for din bestilling, {submissionDetails.name}!</h2>
              <p className="text-xs text-stone-600 font-light leading-relaxed max-w-md mx-auto">
                Vi har modtaget din bestilling og går i gang med at forberede den. Du modtager en bekræftelse fra os hurtigst muligt.
              </p>
            </div>

            {/* Order Summary */}
            <div className="border border-stone-100 bg-stone-50 p-6 text-left space-y-4 rounded-none">
              <h3 className="font-serif text-sm font-medium text-[#1c2b21] border-b border-stone-200 pb-2">Bestillingsoversigt</h3>
              <ul className="space-y-2 text-xs font-light">
                {submissionDetails.items.map((item) => (
                  <li key={item.id} className="flex justify-between">
                    <span>{item.title} <span className="text-stone-400">x {item.quantity}</span></span>
                    <span className="font-medium">{item.price * item.quantity} kr.</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-stone-200 pt-3 flex justify-between text-xs font-semibold uppercase tracking-wider text-[#1c2b21]">
                <span>Total pris</span>
                <span>{submissionDetails.total} kr.</span>
              </div>
              <div className="border-t border-stone-200 pt-3 grid grid-cols-2 gap-4 text-xs font-light">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-stone-400">Afhentningsdato</p>
                  <p className="font-medium text-[#1c2b21] mt-0.5">{submissionDetails.pickupDate}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-stone-400">Tidspunkt</p>
                  <p className="font-medium text-[#1c2b21] mt-0.5">{submissionDetails.pickupTime}</p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-[#b89a6c] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest transition-all hover:bg-[#1c2b21] rounded-none"
              >
                Gå til forsiden <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ) : (
          /* Ordering Interface */
          <div className="grid gap-12 lg:grid-cols-12 items-start">
            {/* Products Column */}
            <div className="lg:col-span-7 space-y-8">
              {/* Category Filter Tabs */}
              <div className="flex flex-wrap gap-2 border-b border-stone-200 pb-4">
                <button
                  onClick={() => setActiveCategory('alle')}
                  className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-all cursor-pointer rounded-none border-b-2 ${
                    activeCategory === 'alle'
                      ? 'border-[#b89a6c] text-[#b89a6c]'
                      : 'border-transparent text-stone-500 hover:text-[#b89a6c]'
                  }`}
                >
                  Alle
                </button>
                <button
                  onClick={() => setActiveCategory('brod-og-boller')}
                  className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-all cursor-pointer rounded-none border-b-2 ${
                    activeCategory === 'brod-og-boller'
                      ? 'border-[#b89a6c] text-[#b89a6c]'
                      : 'border-transparent text-stone-500 hover:text-[#b89a6c]'
                  }`}
                >
                  Brød & Boller
                </button>
                <button
                  onClick={() => setActiveCategory('sodt-og-kager')}
                  className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-all cursor-pointer rounded-none border-b-2 ${
                    activeCategory === 'sodt-og-kager'
                      ? 'border-[#b89a6c] text-[#b89a6c]'
                      : 'border-transparent text-stone-500 hover:text-[#b89a6c]'
                  }`}
                >
                  Sødt & Kager
                </button>
              </div>

              {loading ? (
                <div className="py-20 text-center space-y-3">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#b89a6c]" />
                  <p className="text-xs text-stone-400 font-light uppercase tracking-widest">Henter lækkerier...</p>
                </div>
              ) : filteredProducts.length > 0 ? (
                <div className="grid gap-6 sm:grid-cols-2">
                  {filteredProducts.map((product) => {
                    const qty = cart[product.id] || 0
                    const hasImage = product.image && product.image.length > 0
                    return (
                      <div
                        key={product.id}
                        className="flex flex-col justify-between border border-stone-200 bg-white hover:border-[#b89a6c]/50 transition-colors duration-300 rounded-none overflow-hidden"
                      >
                        <div>
                          <div className="p-6 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-serif text-base font-light text-[#1c2b21] tracking-wide">
                                {product.title}
                              </h3>
                              <span className="text-xs font-semibold text-[#b89a6c] whitespace-nowrap">
                                {product.price} kr.
                              </span>
                            </div>
                            {product.description && (
                              <p className="text-xs text-stone-600 font-light leading-relaxed">
                                {product.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Quantity Selector */}
                        <div className="p-6 pt-0 flex items-center justify-between border-t border-stone-50 mt-auto">
                          <span className="text-[10px] uppercase tracking-wider text-stone-400 font-light">Antal</span>
                          <div className="flex items-center border border-stone-200 bg-stone-50">
                            <button
                              type="button"
                              onClick={() => updateQuantity(product.id, -1)}
                              className="p-2 text-stone-500 hover:text-[#b89a6c] hover:bg-stone-100 transition-colors cursor-pointer"
                              aria-label="Reducer antal"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="px-3 text-xs font-medium text-[#1c2b21] min-w-[24px] text-center">
                              {qty}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(product.id, 1)}
                              className="p-2 text-stone-500 hover:text-[#b89a6c] hover:bg-stone-100 transition-colors cursor-pointer"
                              aria-label="Øg antal"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12 border border-stone-200 bg-white rounded-none">
                  <p className="text-xs text-stone-500 font-light">Ingen produkter fundet i denne kategori.</p>
                </div>
              )}
            </div>

            {/* Cart & Form Column */}
            <div className="lg:col-span-5 space-y-6">
              <div className="border border-stone-200 bg-[#f4f1ea] p-6 md:p-8 rounded-none space-y-6">
                <div className="flex items-center gap-2 border-b border-stone-200 pb-4">
                  <ShoppingBag size={18} className="text-[#b89a6c]" />
                  <h2 className="font-serif text-lg font-light text-[#1c2b21] tracking-wide">Din Bestilling</h2>
                </div>

                {/* Selected Items List */}
                {getCartCount() > 0 ? (
                  <div className="space-y-4">
                    <ul className="divide-y divide-stone-200 max-h-60 overflow-y-auto pr-2 space-y-3">
                      {Object.entries(cart).map(([id, qty]) => {
                        const product = products.find((p) => p.id === id)
                        if (!product) return null
                        return (
                          <li key={id} className="flex items-center justify-between pt-3 first:pt-0 text-xs font-light">
                            <div className="space-y-0.5">
                              <p className="font-medium text-[#1c2b21]">{product.title}</p>
                              <p className="text-[10px] text-stone-500">{product.price} kr. pr. stk</p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center border border-stone-200 bg-white">
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(id, -1)}
                                  className="p-1 text-stone-500 hover:text-[#b89a6c] cursor-pointer"
                                >
                                  <Minus size={10} />
                                </button>
                                <span className="px-2 font-medium text-[#1c2b21]">{qty}</span>
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(id, 1)}
                                  className="p-1 text-stone-500 hover:text-[#b89a6c] cursor-pointer"
                                >
                                  <Plus size={10} />
                                </button>
                              </div>
                              <span className="font-medium text-[#1c2b21] w-16 text-right">
                                {product.price * qty} kr.
                              </span>
                            </div>
                          </li>
                        )
                      })}
                    </ul>

                    {/* Total Price */}
                    <div className="border-t border-stone-200 pt-4 flex justify-between items-center text-sm font-semibold uppercase tracking-wider text-[#1c2b21]">
                      <span>Samlet pris</span>
                      <span className="text-base text-[#b89a6c]">{getCartTotal()} kr.</span>
                    </div>

                    {/* Order Form */}
                    <form onSubmit={handleSubmit} className="border-t border-stone-200 pt-6 space-y-4">
                      {/* Honeypot Field */}
                      <div className="sr-only absolute">
                        <label htmlFor="website_url">Undlad at udfylde dette felt</label>
                        <input
                          id="website_url"
                          type="text"
                          name="website_url"
                          value={formData.website_url}
                          onChange={handleInputChange}
                          tabIndex="-1"
                          autoComplete="off"
                        />
                      </div>

                      {/* Name */}
                      <div className="space-y-1">
                        <label htmlFor="name" className="block text-[10px] uppercase tracking-widest text-stone-500 font-medium">Navn *</label>
                        <div className="relative">
                          <User size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                          <input
                            id="name"
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full border border-stone-200 bg-white py-2.5 pl-10 pr-4 text-xs font-light text-[#1c2b21] focus:border-[#b89a6c] focus:outline-none rounded-none"
                            placeholder="Dit fulde navn"
                          />
                        </div>
                      </div>

                      {/* Phone & Email */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label htmlFor="phone" className="block text-[10px] uppercase tracking-widest text-stone-500 font-medium">Telefon *</label>
                          <div className="relative">
                            <Phone size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                              id="phone"
                              type="tel"
                              name="phone"
                              required
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full border border-stone-200 bg-white py-2.5 pl-10 pr-4 text-xs font-light text-[#1c2b21] focus:border-[#b89a6c] focus:outline-none rounded-none"
                              placeholder="Telefonnummer"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label htmlFor="email" className="block text-[10px] uppercase tracking-widest text-stone-500 font-medium">E-mail *</label>
                          <div className="relative">
                            <Mail size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                              id="email"
                              type="email"
                              name="email"
                              required
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full border border-stone-200 bg-white py-2.5 pl-10 pr-4 text-xs font-light text-[#1c2b21] focus:border-[#b89a6c] focus:outline-none rounded-none"
                              placeholder="E-mailadresse"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Pickup Date & Time */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                          <label htmlFor="pickupDate" className="block text-[10px] uppercase tracking-widest text-stone-500 font-medium">Afhentningsdato *</label>
                          <div className="relative">
                            <Calendar size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                              id="pickupDate"
                              type="date"
                              name="pickupDate"
                              required
                              value={formData.pickupDate}
                              onChange={handleInputChange}
                              className="w-full border border-stone-200 bg-white py-2.5 pl-10 pr-4 text-xs font-light text-[#1c2b21] focus:border-[#b89a6c] focus:outline-none rounded-none"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label htmlFor="pickupTime" className="block text-[10px] uppercase tracking-widest text-stone-500 font-medium">Tidspunkt *</label>
                          <div className="relative">
                            <Clock size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                              id="pickupTime"
                              type="time"
                              name="pickupTime"
                              required
                              value={formData.pickupTime}
                              onChange={handleInputChange}
                              className="w-full border border-stone-200 bg-white py-2.5 pl-10 pr-4 text-xs font-light text-[#1c2b21] focus:border-[#b89a6c] focus:outline-none rounded-none"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Comments */}
                      <div className="space-y-1">
                        <label htmlFor="comments" className="block text-[10px] uppercase tracking-widest text-stone-500 font-medium">Bemærkninger</label>
                        <div className="relative">
                          <MessageSquare size={12} className="absolute left-3.5 top-3 text-stone-400" />
                          <textarea
                            id="comments"
                            name="comments"
                            rows="3"
                            value={formData.comments}
                            onChange={handleInputChange}
                            className="w-full border border-stone-200 bg-white py-2.5 pl-10 pr-4 text-xs font-light text-[#1c2b21] focus:border-[#b89a6c] focus:outline-none rounded-none"
                            placeholder="F.eks. allergener, specielle ønsker eller andet..."
                          />
                        </div>
                      </div>

                      {submitError && (
                        <p className="text-xs text-red-600 font-medium bg-red-50 border border-red-100 p-3">
                          {submitError}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#b89a6c] text-white py-4 text-xs font-semibold uppercase tracking-widest transition-all hover:bg-[#1c2b21] disabled:bg-stone-400 active:scale-95 cursor-pointer rounded-none flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Sender bestilling...
                          </>
                        ) : (
                          'Send bestilling'
                        )}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-3">
                    <div className="inline-flex items-center justify-center p-3 text-stone-300 bg-stone-100 rounded-full">
                      <ShoppingBag size={20} />
                    </div>
                    <p className="text-xs text-stone-500 font-light leading-relaxed max-w-xs mx-auto">
                      Din bestillingsliste er tom. Vælg produkter fra menuen til venstre for at sammensætte din bestilling.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
